import { useState } from "react";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { Button } from "./ui/button";
import { Bell, BellOff, BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * PushNotificationToggle — botão compacto para activar/desactivar push notifications.
 * Usado no StudentDashboard.
 */
const PushNotificationToggle = () => {
  const { supported, permission, subscribed, loading, subscribe, unsubscribe } =
    usePushNotifications();
  const [working, setWorking] = useState(false);

  if (!supported) return null; // Não mostrar se o browser não suporta

  const handleToggle = async () => {
    if (working || loading) return;
    setWorking(true);
    if (subscribed) {
      await unsubscribe();
      toast.info("Notificações desactivadas.");
    } else {
      const result = await subscribe();
      if (result.ok) {
        toast.success("Notificações activadas! Vais ser avisado de novos exames e streak em risco.");
      } else if (result.reason === "denied") {
        toast.error("Permissão negada pelo browser. Activa nas definições do browser.");
      } else {
        toast.error("Erro ao activar notificações. Tenta novamente.");
      }
    }
    setWorking(false);
  };

  const isLoading = loading || working;

  if (permission === "denied") {
    return (
      <div
        className="flex items-center gap-1.5 text-xs text-slate-500 px-2 py-1"
        title="Notificações bloqueadas nas definições do browser"
        data-testid="push-blocked"
      >
        <BellOff className="w-3.5 h-3.5" />
        <span className="sr-only">Notificações bloqueadas</span>
      </div>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleToggle}
      disabled={isLoading}
      data-testid="push-toggle-btn"
      className={`text-xs p-1.5 sm:p-2 transition-colors ${
        subscribed
          ? "text-orange-400 hover:text-orange-300"
          : "text-slate-400 hover:text-slate-200"
      }`}
      title={subscribed ? "Desactivar notificações push" : "Activar notificações push"}
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : subscribed ? (
        <BellRing className="w-4 h-4" />
      ) : (
        <Bell className="w-4 h-4" />
      )}
      <span className="sr-only">
        {subscribed ? "Notificações activas" : "Notificações"}
      </span>
    </Button>
  );
};

export default PushNotificationToggle;
